import { convertServerResponsivePropertyToClientResponsiveProperty } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/responsiveValues";
import { getWidthConstraintCSS } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/Styling/widthConstraint";
import { ComponentLoggingEventParams } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/__internal__/types";
import { SDUIBannerCTARenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Banner/__internal__/SDUIBannerCTARenderer";
import { SDUIButtonRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Button/SDUIButtonRenderer";
import { SDUITypographyRenderer } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/SDUITypographyRenderer";
import { getCssTextAlign } from "@faire/deprecated-retailer-visitor-shared/components/SDUI/ComponentRenderers/Typography/styles/textJustificationStyleUtils";
import {
  assignInlineVars,
  DynamicVariable,
  mapResponsivePropToCSSVars,
} from "@faire/deprecated-retailer-visitor-shared/lib/tailwindUtils";
import { IBannerComponent } from "@faire/web-api--source/indigofair/data/IBannerComponent";
import { Column } from "@faire/web/slate/Layout";
import { Spacer } from "@faire/web/slate/spacing";
import { cn } from "@faire/web/tools/cn";
import React from "react";

const varMobileTitleWidth: DynamicVariable<"--f-sdui-btc-title-width-m"> = {
  variable: "--f-sdui-btc-title-width-m",
  className: "w-(--f-sdui-btc-title-width-m)",
};

const varTabletTitleWidth: DynamicVariable<"--f-sdui-btc-title-width-md"> = {
  variable: "--f-sdui-btc-title-width-md",
  className: "md:w-(--f-sdui-btc-title-width-md)",
};

const varDesktopTitleWidth: DynamicVariable<"--f-sdui-btc-title-width-lg"> = {
  variable: "--f-sdui-btc-title-width-lg",
  className: "lg:w-(--f-sdui-btc-title-width-lg)",
};

const varMobileDescriptionWidth: DynamicVariable<"--f-sdui-btc-description-width-m"> =
  {
    variable: "--f-sdui-btc-description-width-m",
    className: "w-(--f-sdui-btc-description-width-m)",
  };

const varTabletDescriptionWidth: DynamicVariable<"--f-sdui-btc-description-width-md"> =
  {
    variable: "--f-sdui-btc-description-width-md",
    className: "md:w-(--f-sdui-btc-description-width-md)",
  };

const varDesktopDescriptionWidth: DynamicVariable<"--f-sdui-btc-description-width-lg"> =
  {
    variable: "--f-sdui-btc-description-width-lg",
    className: "lg:w-(--f-sdui-btc-description-width-lg)",
  };

export const SDUIBannerTextContent: React.FC<{
  className?: string;
  "data-test-id"?: string;
  bannerComponent: IBannerComponent;
  loggingData: ComponentLoggingEventParams;
  typgraphyHyphens?: React.CSSProperties["hyphens"];
}> = ({
  "data-test-id": dataTestId,
  className,
  bannerComponent,
  loggingData,
  typgraphyHyphens,
}) => {
  const { tagline, title, sub_title, description, button, style, disclaimer } =
    bannerComponent;

  const {
    description_width,
    responsive_description_width,
    header_width,
    responsive_header_width,
  } = style ?? {};

  const responsiveHeaderWidth =
    convertServerResponsivePropertyToClientResponsiveProperty(
      header_width,
      getWidthConstraintCSS,
      responsive_header_width
    );

  const { vars: headerWidthVars, classes: headerWidthClasses } =
    mapResponsivePropToCSSVars(responsiveHeaderWidth, {
      mobileAndAbove: varMobileTitleWidth,
      tabletAndAbove: varTabletTitleWidth,
      desktopAndAbove: varDesktopTitleWidth,
    });

  const responsiveDescriptionWidth =
    convertServerResponsivePropertyToClientResponsiveProperty(
      description_width,
      getWidthConstraintCSS,
      responsive_description_width
    );

  const { vars: descriptionWidthVars, classes: descriptionWidthClasses } =
    mapResponsivePropToCSSVars(responsiveDescriptionWidth, {
      mobileAndAbove: varMobileDescriptionWidth,
      tabletAndAbove: varTabletDescriptionWidth,
      desktopAndAbove: varDesktopDescriptionWidth,
    });

  return (
    <Column
      className={cn(className, "relative")}
      data-test-id={dataTestId}
      justify="center"
    >
      {tagline ? (
        <>
          <SDUITypographyRenderer
            textElement={tagline}
            loggingData={loggingData}
            componentStyle={{
              hyphens: typgraphyHyphens,
              alignSelf: getCssTextAlign(tagline.style?.text_justification),
            }}
          />
          <Spacer height={{ mobileAndAbove: "1x", tabletAndAbove: "2x" }} />
        </>
      ) : null}
      {title ? (
        <SDUITypographyRenderer
          className={cn(headerWidthClasses)}
          componentStyle={{
            hyphens: typgraphyHyphens,
            ...assignInlineVars(headerWidthVars),
            alignSelf: getCssTextAlign(title.style?.text_justification),
          }}
          textElement={title}
          loggingData={loggingData}
        />
      ) : null}
      {sub_title ? (
        <>
          <Spacer height="1x" />
          <SDUITypographyRenderer
            textElement={sub_title}
            loggingData={loggingData}
            componentStyle={{
              hyphens: typgraphyHyphens,
              alignSelf: getCssTextAlign(sub_title.style?.text_justification),
            }}
          />
        </>
      ) : null}
      {description.length > 0 ? (
        <>
          <Spacer height={{ mobileAndAbove: "1x", tabletAndAbove: "2x" }} />
          {description.map((descriptionElement, index) => (
            <React.Fragment key={index}>
              {index > 0 ? <Spacer height="1/2x" /> : null}
              <SDUITypographyRenderer
                className={cn(descriptionWidthClasses)}
                componentStyle={{
                  hyphens: typgraphyHyphens,
                  ...assignInlineVars(descriptionWidthVars),
                  alignSelf: getCssTextAlign(
                    descriptionElement.style?.text_justification
                  ),
                }}
                textElement={descriptionElement}
                loggingData={loggingData}
              />
            </React.Fragment>
          ))}
        </>
      ) : null}
      {button ? (
        <>
          <Spacer height="2x" />
          <SDUIButtonRenderer
            buttonElement={button}
            loggingData={loggingData}
            fireLegacyOnClickEvent
            width={{ mobileAndAbove: "100%", tabletAndAbove: "fit-content" }}
            minWidth={{ mobileAndAbove: "auto", tabletAndAbove: "200px" }}
          />
        </>
      ) : null}
      {disclaimer ? (
        <>
          <Spacer height="2x" />
          <SDUITypographyRenderer
            textElement={disclaimer}
            loggingData={loggingData}
            componentStyle={{
              hyphens: typgraphyHyphens,
              alignSelf: getCssTextAlign(disclaimer.style?.text_justification),
            }}
          />
        </>
      ) : null}
      <SDUIBannerCTARenderer bannerComponent={bannerComponent} />
    </Column>
  );
};
